import {
  MAX_QUICK_SESSION_DURATION_MINUTES,
  MAX_TARGETS_PER_RULE,
  normalizeQuickSessionWindow,
} from "../../lib/rules/ruleDerivation";
import {
  getMinuteOfDay,
  wouldRuleEnforceIgnoringSuppression,
  type DeviceScheduleClock,
} from "../../lib/rules/ruleSchedule";
import type { Rule, RuleConfig, RuleWindow } from "../../lib/rules/ruleTypes";

export const QUICK_SESSION_SOURCE = "quick_session" as const;
export const MAX_RECENT_SESSIONS = 6;
export const MAX_SAVED_SESSIONS = 6;
export const DEFAULT_QUICK_DURATION_MINUTES = 120;
export const QUICK_SESSION_BACKUP_CHECK_MS = 30_000;
export const DEFAULT_FAB_ICON = "Zap";

export type QuickSessionEntry = {
  targetIds: string[];
  durationMinutes: number;
  autoName: string;
  lastUsedAt: number;
};

export type SavedQuickSession = {
  name: string;
  targetIds: string[];
  durationMinutes: number;
  savedAt: number;
};

export function isQuickSessionRule(rule: { source?: Rule["source"] }): boolean {
  return rule.source === QUICK_SESSION_SOURCE;
}

/** Rules shown in the "Scheduled rules" list — excludes ephemeral quick sessions. */
export function filterVisibleRules<T extends { source?: Rule["source"] }>(rules: T[]): T[] {
  return rules.filter((rule) => !isQuickSessionRule(rule));
}

export function findActiveQuickSession(rules: Rule[]): Rule | undefined {
  return rules.find((rule) => isQuickSessionRule(rule) && rule.enabled);
}

/** True while the quick session window is still open — ignores break/full-stop suppression. */
export function isQuickSessionScheduleActive(rule: RuleConfig, now: Date = new Date()): boolean {
  if (!isQuickSessionRule(rule) || !rule.enabled) return false;
  const clock: DeviceScheduleClock = {
    dayOfWeek: now.getDay(),
    minuteOfDay: getMinuteOfDay(now),
    nowMs: now.getTime(),
  };
  return wouldRuleEnforceIgnoringSuppression(rule, clock);
}

export function buildQuickSessionConfig(
  now: Date,
  targetIds: string[],
  durationMinutes: number,
): RuleConfig {
  const startMinute = now.getHours() * 60 + now.getMinutes();
  return {
    enabled: true,
    targetIds: targetIds.slice(0, MAX_TARGETS_PER_RULE),
    days: [now.getDay()],
    window: normalizeQuickSessionWindow({ startMinute, durationMinutes }),
    source: QUICK_SESSION_SOURCE,
  };
}

/** Quick sessions allow a fixed duration cap regardless of time of day (unlike scheduled rules). */
export function maxQuickDurationMinutes(): number {
  return MAX_QUICK_SESSION_DURATION_MINUTES;
}

/** setMinutes() overflows past 59 by rolling the Date into following days, so this is correct even when endMinute >= MINUTES_PER_DAY (a quick session spanning midnight). */
export function sessionEndTimestamp(window: RuleWindow, referenceDate: Date = new Date()): number {
  const endMinute = window.startMinute + window.durationMinutes;
  const end = new Date(referenceDate);
  end.setHours(0, 0, 0, 0);
  end.setMinutes(endMinute);
  return end.getTime();
}

export function msUntilSessionEnd(window: RuleWindow, referenceDate: Date = new Date()): number {
  return Math.max(0, sessionEndTimestamp(window, referenceDate) - referenceDate.getTime());
}

export function sessionFingerprint(targetIds: string[], durationMinutes: number): string {
  return [...targetIds].sort().join(",") + ":" + durationMinutes;
}

export function isSessionSaved(
  savedSessions: SavedQuickSession[],
  targetIds: string[],
  durationMinutes: number,
): boolean {
  const fingerprint = sessionFingerprint(targetIds, durationMinutes);
  return savedSessions.some(
    (entry) => sessionFingerprint(entry.targetIds, entry.durationMinutes) === fingerprint,
  );
}

export function capSessions<T>(sessions: T[], max: number): T[] {
  return sessions.slice(0, max);
}

export function buildAutoSessionName(
  targetIds: string[],
  targetsById: Map<string, { name: string }>,
  fallback: string,
): string {
  const names = targetIds
    .map((id) => targetsById.get(id)?.name)
    .filter((name): name is string => Boolean(name?.trim()));
  return names.length > 0 ? names.join(", ") : fallback;
}

export function areTargetIdsAvailable(
  targetIds: string[],
  targetsById: Map<string, unknown>,
): boolean {
  return targetIds.length > 0 && targetIds.every((id) => targetsById.has(id));
}

/** Drop deleted or unknown target ids while preserving order (e.g. quick-start selection). */
export function pruneToAvailableTargetIds(
  targetIds: string[],
  targetsById: Map<string, unknown>,
): string[] {
  return targetIds.filter((id) => targetsById.has(id));
}

/** Remove a deleted target from quick-start history entries; drop entries with no targets left. */
export function purgeTargetFromQuickStartSessions<
  TRecent extends { targetIds: string[] },
  TSaved extends { targetIds: string[] },
>(
  recentSessions: TRecent[],
  savedSessions: TSaved[],
  removedTargetId: string,
): { recentSessions: TRecent[]; savedSessions: TSaved[] } {
  const prune = <T extends { targetIds: string[] }>(sessions: T[]): T[] =>
    sessions
      .map((entry) => ({
        ...entry,
        targetIds: entry.targetIds.filter((id) => id !== removedTargetId),
      }))
      .filter((entry) => entry.targetIds.length > 0);

  return {
    recentSessions: prune(recentSessions),
    savedSessions: prune(savedSessions),
  };
}

export function sanitizeQuickDuration(durationMinutes: number): number {
  const rounded = Math.round(durationMinutes / 5) * 5;
  return normalizeQuickSessionWindow({ startMinute: 0, durationMinutes: rounded }).durationMinutes;
}
